import React from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import {
    LayoutDashboard, Home, Users, FileText,
    Calculator, Building2, BookOpen, Settings,
    CreditCard, LogOut, ChevronRight, Crown, Plus
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';

export default function DashboardSidebar({ className, isOpen, onClose }) {
    const { user, logout } = useAuth();
    const navigate = useNavigate();

    const handleLogout = () => {
        logout();
        navigate('/login');
    };

    const menuGroups = [
        {
            title: "MI NEGOCIO",
            items: [
                { title: "Dashboard", icon: LayoutDashboard, href: "/dashboard" },
                { title: "Mis Propiedades", icon: Home, href: "/properties" },
                { title: "Publicar Inmueble", icon: Plus, href: "/properties/create" },
                { title: "Gestión de Leads", icon: Users, href: "/leads" },
                { title: "Documentos", icon: FileText, href: "/documents" },
            ]
        },
        {
            title: "MARKETING",
            items: [
                { title: "Generador de Posts", icon: Crown, href: "/social-generator" },
                { title: "Publicador Social", icon: Users, href: "/marketing" },
            ]
        },
        {
            title: "HERRAMIENTAS",
            items: [
                { title: "Centro Financiero", icon: Calculator, href: "/centro-financiero" },
                { title: "Catálogo Público", icon: Building2, href: "/propiedades" },
                { title: "Conocimiento", icon: BookOpen, href: "/knowledge" },
            ]
        },
        {
            title: "CONFIGURACIÓN",
            items: [
                { title: "Ajustes & Perfil", icon: Settings, href: "/settings" },
                { title: "Facturación", icon: CreditCard, href: "/billing" },
            ]
        }
    ];

    return (
        <>
            {/* Mobile Overlay */}
            {isOpen && (
                <div
                    className="fixed inset-0 bg-black/50 backdrop-blur-sm z-30 lg:hidden"
                    onClick={onClose}
                />
            )}

            <div className={cn(
                "pb-12 min-h-screen w-64 border-r border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 flex flex-col justify-between fixed left-0 top-0 bottom-0 z-40 transition-transform duration-300 lg:translate-x-0 shadow-2xl lg:shadow-none",
                isOpen ? "translate-x-0" : "-translate-x-full",
                className
            )}>
                <div className="space-y-4 py-4">
                    <div className="px-6 py-2">
                        <h2 className="text-xl font-bold tracking-tight flex items-center gap-2 text-slate-900 dark:text-white">
                            Servicios <span className="text-blue-600">Futura</span>
                        </h2>
                        {user?.plan === 'pro' && (
                            <div className="mt-2 flex items-center gap-1 bg-gradient-to-r from-amber-200 to-yellow-400 text-yellow-900 px-2 py-0.5 rounded text-[10px] font-bold w-fit">
                                <Crown className="w-3 h-3" /> AGENTE PRO
                            </div>
                        )}
                    </div>

                    <div className="px-3 py-2">
                        {menuGroups.map((group, i) => (
                            <div key={i} className="mb-8">
                                <h3 className="mb-2 px-4 text-xs font-bold text-slate-400 dark:text-slate-500 tracking-wider">
                                    {group.title}
                                </h3>
                                <div className="space-y-1">
                                    {group.items.map((item) => (
                                        <NavLink
                                            key={item.href}
                                            to={item.href}
                                            className={({ isActive }) => cn(
                                                "flex items-center gap-3 rounded-lg px-4 py-2.5 text-sm font-medium transition-all",
                                                isActive
                                                    ? "bg-blue-600 text-white shadow-lg shadow-blue-500/20 font-bold border-l-4 border-white"
                                                    : "text-slate-600 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800 hover:text-slate-900 dark:hover:text-slate-200"
                                            )}
                                        >
                                            <item.icon className="h-4 w-4" />
                                            {item.title}
                                        </NavLink>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* User Footer */}
                <div className="px-4 py-4 border-t border-slate-100 dark:border-slate-800 bg-slate-50 dark:bg-slate-900/50">
                    <div className="flex items-center gap-3 mb-4">
                        <Avatar className="h-9 w-9 border border-slate-200 dark:border-slate-700">
                            <AvatarImage src={user?.photoURL} alt={user?.full_name} />
                            <AvatarFallback className="bg-blue-100 text-blue-700 font-bold">
                                {user?.full_name?.charAt(0) || 'U'}
                            </AvatarFallback>
                        </Avatar>
                        <div className="flex-1 overflow-hidden">
                            <p className="text-xs font-semibold text-slate-900 dark:text-slate-200 truncate">
                                {user?.full_name || 'Usuario'}
                            </p>
                            <p className="text-[10px] text-slate-500 dark:text-slate-400 truncate">
                                {user?.email}
                            </p>
                        </div>
                    </div>
                    <Button
                        variant="outline"
                        onClick={handleLogout}
                        className="w-full justify-start text-xs text-red-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/10 border-red-100 dark:border-red-900/30 bg-transparent"
                    >
                        <LogOut className="mr-2 h-3 w-3" /> Cerrar Sesión
                    </Button>
                </div>
            </div>
        </>
    );
}
